/**
 * Dashboard "wow-grade" (F16).
 *
 * Vista commerciale + operativa pensata per il titolare di una impresa di
 * cartongesso. Mostra in un colpo d'occhio:
 *   - Pipeline preventivi (Bozza → Inviato → Vinto/Perso, F30)
 *   - Conversion rate + tempo medio chiusura
 *   - Trend mensile valore vinto vs preventivato (ultimi 12m)
 *   - Top clienti & top stratigrafie del periodo selezionato
 *   - Alert preventivi inviati > 30gg senza risposta
 *   - Breakdown materiali per categoria/fornitore
 *   - Filtro periodo applicato a tutte le metriche di "flusso"
 *
 * Per le metriche di stato corrente (progetti attivi, fornitori in
 * anagrafica) il filtro periodo non si applica — il dato è "ora".
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, ComposedChart, Line,
} from 'recharts';
import {
  TrendingUp, Users, FileText, Euro, Clock, Package, Wrench, AlertTriangle,
  Target, Factory, Trophy, XCircle, Send, FileEdit, Calendar as CalendarIcon, Percent,
  ArrowRight, Layers, Settings2, RefreshCw, CalendarClock, AlarmClock, Download, Crosshair,
} from 'lucide-react';

import { useDashboardData, type DashboardPeriod, type DashboardCustomRange } from '@/hooks/useDashboardData';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useOrgProfile } from '@/hooks/useOrgSettings';
import { applyPricing, type CostBase } from '@/utils/pricing/applyPricing';
import { computeEstimateCostBase, rowCostOptionsFrom } from '@/utils/pricing/computeEstimateCostBase';
import { useDashboardPreferences, type HeroCardId, type DashboardTabId } from '@/hooks/useDashboardPreferences';
import { useRefreshAllStaleEstimates } from '@/hooks/useRefreshAllStaleEstimates';
import { DashboardFiltersProvider, useDashboardFilters, estimateMatchesFilters } from '@/contexts/DashboardFiltersContext';
import { DashboardCard } from './DashboardCard';
import { MaterialsBreakdownCard } from './MaterialsBreakdownCard';
import { AnimatedCounter } from '@/components/ui/animated-counter';
import { AnimatedProgress } from '@/components/ui/animated-progress';
import DashboardDrillDownSheet, { type DrillDownEstimate } from './DashboardDrillDownSheet';
import DashboardCustomizeSheet from './DashboardCustomizeSheet';
import DashboardActivityFeed from './DashboardActivityFeed';
import DashboardFilterBar from './DashboardFilterBar';
import DashboardFollowUpWidget from './DashboardFollowUpWidget';
import { formatNumber } from '@/utils/format';

const COLORS = ['#1E5091', '#3478BE', '#5696D7', '#84B4E5', '#A8C5E8', '#CCE0F0'];
const FUNNEL_COLORS = {
  draft: '#94a3b8',    // slate-400
  sent: '#f59e0b',     // amber-500
  won: '#10b981',      // emerald-500
  lost: '#ef4444',     // red-500
};

function euro(n: number): string {
  return `€ ${Math.round(n).toLocaleString('it-IT')}`;
}

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  'all': 'Tutto lo storico',
  '1m': 'Ultimo mese',
  '3m': 'Ultimi 3 mesi',
  '6m': 'Ultimi 6 mesi',
  '12m': 'Ultimi 12 mesi',
  'ytd': 'Da inizio anno',
  'custom': 'Periodo personalizzato…',
};

/** Ordine visualizzato nel select: "Tutto lo storico" in cima per accessibilità rapida. */
const PERIOD_ORDER: DashboardPeriod[] = ['all', '1m', '3m', '6m', '12m', 'ytd', 'custom'];

/**
 * F16.1: configurazione del drill-down sheet aperto.
 * Quando l'utente clicca su una KPI / riga / segmento, popoliamo questo
 * stato e il DashboardDrillDownSheet sotto si apre con i preventivi
 * filtrati corrispondenti.
 */
interface DrillDownConfig {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  estimates: DrillDownEstimate[];
}

const DEFAULT_PERIOD: DashboardPeriod = '6m';

const EnhancedDashboardOverviewInner = () => {
  const { currentRole } = useCurrentOrganization();
  const { data: orgProfile } = useOrgProfile();
  const canSeeMargins = currentRole === 'owner' || currentRole === 'admin';
  const { filters, toggleStatus, toggleCustomer, setStratigraphy, setSupplier, hasActiveFilters, resetFilters } = useDashboardFilters();
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_PERIOD);
  const [customRange, setCustomRange] = useState<DashboardCustomRange>({ from: null, to: null });
  const [customRangeOpen, setCustomRangeOpen] = useState(false);

  /** Reset completo: periodo a default + range custom vuoto + cross-filters azzerati. */
  const handleResetAll = () => {
    setPeriod(DEFAULT_PERIOD);
    setCustomRange({ from: null, to: null });
    resetFilters();
  };

  /** True se qualcosa è "non-default": periodo diverso da default OR filtri attivi OR range custom impostato. */
  const isDashboardCustomized = period !== DEFAULT_PERIOD || hasActiveFilters || !!customRange.from;

  /**
   * Esporta come CSV la lista preventivi filtrati. Una riga per preventivo
   * con i campi principali. Apre direttamente il download nel browser senza
   * dipendenze (usa Blob + anchor invisibile).
   */
  const exportFilteredAsCsv = () => {
    const rows = filteredEstimatesForWidgets;
    if (rows.length === 0) return;
    const escapeCsv = (v: unknown): string => {
      const s = v == null ? '' : String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const statusLabel = (s: string): string => ({
      draft: 'Bozza', sent: 'Inviato', pending: 'Inviato',
      won: 'Vinto', contracted: 'Vinto', approved: 'Vinto',
      lost: 'Perso',
    }[s] ?? s);
    const header = [
      'ID', 'Numero offerta', 'Nome', 'Progetto', 'Cliente',
      'Stato', 'Importo €', 'Versione', 'Creato il', 'Inviato il', 'Vinto il', 'Perso il',
    ];
    const lines = [header.join(',')];
    for (const e of rows) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = e as any;
      const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleDateString('it-IT') : '';
      lines.push([
        r.id,
        r.offer_number ?? '',
        r.name ?? '',
        r.projects?.name ?? '',
        r.projects?.customers?.name ?? r.projects?.client ?? '',
        statusLabel(r.status),
        Math.round(Number(r.total_amount ?? 0)),
        r.version ?? 1,
        fmt(r.created_at),
        fmt(r.sent_at),
        fmt(r.won_at),
        fmt(r.lost_at),
      ].map(escapeCsv).join(','));
    }
    // BOM utf-8 per Excel italiano
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    a.download = `dashboard-preventivi-${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const { dashboardMetrics: m, topCustomers, topCustomersByWinRate, previousPeriodComparison: prev, customersData, isLoading, estimatesData, staleDaysThreshold } = useDashboardData(period, filters, customRange);
  const [drillDown, setDrillDown] = useState<DrillDownConfig | null>(null);

  /**
   * Helper: dato current e previous, ritorna l'oggetto `change` da passare a DashboardCard.
   * `inversedDelta` true per metriche dove "scendere è meglio" (es. tempo medio chiusura, valore perso).
   * Restituisce null se non c'è un previous sensato da confrontare.
   */
  const buildChange = (
    current: number,
    previous: number | null | undefined,
    options?: { inversedDelta?: boolean; precision?: number },
  ): { value: number; type: 'positive' | 'negative' | 'neutral'; label: string } | undefined => {
    if (previous == null || !prev) return undefined;
    const inversed = options?.inversedDelta ?? false;
    if (previous === 0 && current === 0) return undefined;
    if (previous === 0) {
      return { value: 100, type: inversed ? 'negative' : 'positive', label: 'vs precedente (nuovo)' };
    }
    const delta = ((current - previous) / Math.abs(previous)) * 100;
    let type: 'positive' | 'negative' | 'neutral' = 'neutral';
    if (Math.abs(delta) < 1) type = 'neutral';
    else if (delta > 0) type = inversed ? 'negative' : 'positive';
    else type = inversed ? 'positive' : 'negative';
    return { value: delta, type, label: 'vs precedente' };
  };

  /**
   * Click su una colonna del trend mensile: imposta period='custom' e
   * range = inizio/fine di quel mese. Cross-filter su tutta la dashboard.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const drillToMonth = (chartEvent: any) => {
    const payload = chartEvent?.activePayload?.[0]?.payload;
    if (!payload?.monthStartIso || !payload?.monthEndIso) return;
    setPeriod('custom');
    setCustomRange({
      from: new Date(payload.monthStartIso),
      to: new Date(payload.monthEndIso),
    });
  };

  // Label visualizzata accanto al select quando si è in modalità custom.
  const customRangeLabel = (() => {
    if (period !== 'custom') return null;
    const { from, to } = customRange;
    if (!from && !to) return 'Imposta range';
    const f = from ? format(from, 'd MMM yy', { locale: itLocale }) : '…';
    const t = to ? format(to, 'd MMM yy', { locale: itLocale }) : '…';
    return `${f} → ${t}`;
  })();
  // F34: preferenze dashboard (ordine card hero + visibilità card/tab)
  const { layout, saveLayout, resetLayout, isSaving: isSavingLayout } = useDashboardPreferences();
  const [openCustomize, setOpenCustomize] = useState(false);
  // Bulk "Aggiorna tutti i preventivi disallineati"
  const { refreshAll, isRefreshing } = useRefreshAllStaleEstimates();

  // ============================================================
  // F16.1: utilità per il drill-down
  // ============================================================
  // NOTE: questi hooks (useMemo) DEVONO stare PRIMA di qualsiasi early return,
  // altrimenti il numero di hook cambia tra render in loading e render con dati
  // → Rules of Hooks violation ("Rendered more hooks than during the previous render").
  // Filtro coerente con useDashboardData: applichiamo lo stesso periodo
  // per evitare che la dashboard mostri "5 vinti" e il sheet "8".
  const periodCutoff = (() => {
    if (period === 'all') return null;
    if (period === 'custom') return customRange.from;
    const now = new Date();
    if (period === 'ytd') return new Date(now.getFullYear(), 0, 1);
    const monthMap: Record<string, number> = { '1m': 1, '3m': 3, '6m': 6, '12m': 12 };
    const months = monthMap[period];
    if (!months) return null;
    const cutoff = new Date(now);
    cutoff.setMonth(cutoff.getMonth() - months);
    return cutoff;
  })();

  /**
   * Lista preventivi filtrati (periodo + cross-filters). Riusata per i widget
   * che mostrano dettagli "live" come Activity feed + breakdown materiali
   * espandibile. Stessa logica di useDashboardData incluso il range custom.
   */
  const filteredEstimatesForWidgets = React.useMemo(() => {
    if (!estimatesData) return [];
    const customTo = period === 'custom' && customRange.to ? (() => {
      const d = new Date(customRange.to);
      d.setHours(23, 59, 59, 999);
      return d;
    })() : null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (estimatesData as any[])
      .filter(e => {
        const created = new Date(e.created_at);
        if (periodCutoff && created < periodCutoff) return false;
        if (customTo && created > customTo) return false;
        return true;
      })
      .filter(e => estimateMatchesFilters(e, filters));
  }, [estimatesData, periodCutoff, filters, period, customRange.to]);

  /**
   * Margine % medio sulla finestra filtrata (vista gestionale).
   * Replica la pipeline costo→prezzo cliente di SellingPriceCard:
   * costBase = materials + screws + labor + finish; netPrice via applyPricing.
   * Margin % = (netPrice - costBase) / costBase * 100.
   */
  const marginPctAvg = React.useMemo<number | null>(() => {
    if (!canSeeMargins) return null;
    if (filteredEstimatesForWidgets.length === 0) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tutteLeRighe = (filteredEstimatesForWidgets as any[]).flatMap(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (e) => ((e?.estimate_stratigraphies as any[] | null) ?? []),
    );
    const costBase: CostBase = computeEstimateCostBase(tutteLeRighe, rowCostOptionsFrom(orgProfile));
    if (costBase.materials + costBase.labor + costBase.finish <= 0) return null;
    const breakdown = applyPricing(costBase, orgProfile ?? null);
    if (breakdown.costBase <= 0) return null;
    return ((breakdown.netPrice - breakdown.costBase) / breakdown.costBase) * 100;
  }, [filteredEstimatesForWidgets, canSeeMargins, orgProfile]);

  // Early return: dopo tutti gli hooks (Rules of Hooks). Mostra skeleton durante
  // il primo caricamento di useDashboardData.
  if (isLoading || !m) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-gray-200 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const hasAnyEstimate = m.totalEstimates > 0;
  const funnelData = [
    { name: 'Bozze', value: m.draftEstimates, fill: FUNNEL_COLORS.draft, statuses: ['draft'] as string[] },
    { name: 'Inviati', value: m.pendingEstimates, fill: FUNNEL_COLORS.sent, statuses: ['sent', 'pending'] as string[] },
    { name: 'Vinti', value: m.contractedEstimates, fill: FUNNEL_COLORS.won, statuses: ['won', 'contracted'] as string[] },
    { name: 'Persi', value: m.lostEstimates, fill: FUNNEL_COLORS.lost, statuses: ['lost'] as string[] },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterEstimatesByStatuses = (statuses: string[]): DrillDownEstimate[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (estimatesData as any[])
      .filter(e => {
        if (!statuses.includes(e.status)) return false;
        if (periodCutoff && new Date(e.created_at) < periodCutoff) return false;
        return true;
      })
      .map(e => ({
        id: e.id,
        name: e.name || 'Preventivo',
        status: e.status,
        total_amount: e.total_amount,
        created_at: e.created_at,
        projects: e.projects ? { name: e.projects.name } : null,
      }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allInPeriod = (): DrillDownEstimate[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (estimatesData as any[])
      .filter(e => !periodCutoff || new Date(e.created_at) >= periodCutoff)
      .map(e => ({
        id: e.id,
        name: e.name || 'Preventivo',
        status: e.status,
        total_amount: e.total_amount,
        created_at: e.created_at,
        projects: e.projects ? { name: e.projects.name } : null,
      }));
  };

  const openStatusDrill = (statuses: string[], title: string, description: string, icon: React.ReactNode) => {
    setDrillDown({
      title,
      description,
      icon,
      estimates: filterEstimatesByStatuses(statuses),
    });
  };

  const openLostReasonDrill = (reasonLabel: string) => {
    // Mappa label → reason key (inverso del LOST_REASON_LABELS di useDashboardData).
    const labelToKey: Record<string, string> = {
      'Prezzo troppo alto': 'price',
      'Tempi non compatibili': 'timing',
      'Concorrente scelto': 'competitor',
      'Nessuna risposta': 'no_response',
      'Altro motivo': 'other',
    };
    const reasonKey = labelToKey[reasonLabel] ?? reasonLabel;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtered = (estimatesData as any[])
      .filter(e => {
        if (e.status !== 'lost') return false;
        if ((e.lost_reason ?? 'other') !== reasonKey) return false;
        if (periodCutoff && new Date(e.created_at) < periodCutoff) return false;
        return true;
      })
      .map(e => ({
        id: e.id,
        name: e.name || 'Preventivo',
        status: e.status,
        total_amount: e.total_amount,
        created_at: e.created_at,
        projects: e.projects ? { name: e.projects.name } : null,
      }));
    setDrillDown({
      title: `Persi: ${reasonLabel}`,
      description: 'Preventivi marcati come persi con questa motivazione',
      icon: <XCircle className="h-5 w-5 text-red-600" />,
      estimates: filtered,
    });
  };

  const openStratigraphyDrill = (stratName: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtered = (estimatesData as any[])
      .filter(e => {
        if (periodCutoff && new Date(e.created_at) < periodCutoff) return false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const stratis = (e.estimate_stratigraphies as any[] | null) ?? [];
        return stratis.some(s => (s.name || 'Senza nome') === stratName);
      })
      .map(e => ({
        id: e.id,
        name: e.name || 'Preventivo',
        status: e.status,
        total_amount: e.total_amount,
        created_at: e.created_at,
        projects: e.projects ? { name: e.projects.name } : null,
      }));
    setDrillDown({
      title: `Stratigrafia: ${stratName}`,
      description: 'Preventivi che contengono questa stratigrafia',
      icon: <Layers className="h-5 w-5 text-blue-600" />,
      estimates: filtered,
    });
  };

  const openCustomerDrill = (customerId: string, customerName: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtered = (estimatesData as any[])
      .filter(e => {
        if (periodCutoff && new Date(e.created_at) < periodCutoff) return false;
        const isWon = e.status === 'won' || e.status === 'contracted';
        if (!isWon) return false;
        return e.projects?.customer_id === customerId;
      })
      .map(e => ({
        id: e.id,
        name: e.name || 'Preventivo',
        status: e.status,
        total_amount: e.total_amount,
        created_at: e.created_at,
        projects: e.projects ? { name: e.projects.name } : null,
      }));
    setDrillDown({
      title: `Cliente: ${customerName}`,
      description: 'Preventivi vinti con questo cliente nel periodo',
      icon: <Trophy className="h-5 w-5 text-emerald-600" />,
      estimates: filtered,
    });
  };

  return (
    <div className="space-y-6">
      {/* ============================================================
          HEADER + FILTRO PERIODO
          ============================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Dati del periodo: <span className="font-medium">{PERIOD_LABELS[period]}</span>
            {' · '}
            <span className="text-xs">i valori "in corso" (es. progetti attivi) sono sempre in tempo reale</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={period}
            onValueChange={(v) => {
              setPeriod(v as DashboardPeriod);
              if (v === 'custom') setCustomRangeOpen(true);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_ORDER.map(p => (
                <SelectItem key={p} value={p}>{PERIOD_LABELS[p]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date range picker — visibile solo quando period === 'custom' */}
          {period === 'custom' && (
            <Popover open={customRangeOpen} onOpenChange={setCustomRangeOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {customRangeLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  defaultMonth={customRange.from ?? new Date()}
                  selected={{
                    from: customRange.from ?? undefined,
                    to: customRange.to ?? undefined,
                  } as DateRange}
                  onSelect={(range: DateRange | undefined) => {
                    setCustomRange({
                      from: range?.from ?? null,
                      to: range?.to ?? null,
                    });
                  }}
                  locale={itLocale}
                />
                <div className="p-2 border-t flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCustomRange({ from: null, to: null })}
                  >
                    Pulisci
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setCustomRangeOpen(false)}
                    disabled={!customRange.from}
                  >
                    Applica
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Reset filtri + periodo (sempre visibile, attivo solo se dashboard customizzata) */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleResetAll}
            disabled={!isDashboardCustomized}
            title={isDashboardCustomized
              ? 'Azzera periodo, range e filtri'
              : 'Dashboard già allo stato di default'}
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>

          {/* Esporta CSV dei preventivi correntemente filtrati */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={exportFilteredAsCsv}
            disabled={filteredEstimatesForWidgets.length === 0}
            title="Scarica i preventivi filtrati come CSV (Excel)"
          >
            <Download className="h-4 w-4" />
            Esporta CSV
          </Button>

          {/* F34: bottone personalizza dashboard */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setOpenCustomize(true)}
            aria-label="Personalizza dashboard"
            title="Personalizza dashboard"
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ============================================================
          FILTER BAR — cross-filtering PowerBI-style
          ============================================================ */}
      <DashboardFilterBar
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        customersData={customersData as any[]}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        estimatesData={estimatesData as any[]}
      />

      {/* ============================================================
          HERO ROW — 6 KPI COMMERCIALI (ordine + visibilità da F34 layout)
          ============================================================ */}
      {(() => {
        const heroCards: Record<HeroCardId, React.ReactNode> = {
          won: (
            <DashboardCard
              key="won"
              title="Vinti nel periodo"
              value={<AnimatedCounter value={m.contractedValue} prefix="€ " decimals={0} />}
              description={`${m.contractedEstimates} preventivi confermati`}
              icon={<Trophy className="text-emerald-600" />}
              className="border-emerald-200/50 bg-gradient-to-br from-emerald-50/60 to-background"
              change={buildChange(m.contractedValue, prev?.wonValue)}
              onClick={() => openStatusDrill(
                ['won', 'contracted'],
                'Preventivi vinti',
                `Preventivi confermati nel periodo: ${PERIOD_LABELS[period]}`,
                <Trophy className="h-5 w-5 text-emerald-600" />,
              )}
            />
          ),
          pipeline: (
            <DashboardCard
              key="pipeline"
              title="Pipeline aperta"
              value={<AnimatedCounter value={m.pendingValue} prefix="€ " decimals={0} />}
              description={`${m.pendingEstimates} preventivi in attesa risposta`}
              icon={<Send className="text-amber-600" />}
              className="border-amber-200/50 bg-gradient-to-br from-amber-50/60 to-background"
              change={buildChange(m.pendingValue, prev?.pendingValue)}
              onClick={() => openStatusDrill(
                ['sent', 'pending'],
                'Pipeline aperta',
                'Preventivi inviati al cliente in attesa di risposta',
                <Send className="h-5 w-5 text-amber-600" />,
              )}
            />
          ),
          conversion: (
            <DashboardCard
              key="conversion"
              title="Conversion rate"
              value={<AnimatedCounter value={m.conversionRate} suffix=" %" decimals={0} />}
              description={`${m.contractedEstimates} vinti su ${m.contractedEstimates + m.lostEstimates} chiusi`}
              icon={<Percent className="text-blue-600" />}
              change={buildChange(m.conversionRate, prev?.conversionRate)}
              onClick={() => openStatusDrill(
                ['won', 'contracted', 'lost'],
                'Preventivi chiusi',
                'Tutti i preventivi su cui il cliente ha già deciso (vinti + persi)',
                <Percent className="h-5 w-5 text-blue-600" />,
              )}
            />
          ),
          avg_close: (
            <DashboardCard
              key="avg_close"
              title="Tempo medio chiusura"
              value={<AnimatedCounter value={m.avgDaysToClose} suffix=" gg" decimals={0} />}
              description="Giorni da invio a vinto/perso"
              icon={<Clock className="text-indigo-600" />}
              change={buildChange(m.avgDaysToClose, prev?.avgDaysToClose, { inversedDelta: true })}
              onClick={() => openStatusDrill(
                ['won', 'contracted', 'lost'],
                'Preventivi chiusi',
                'Preventivi conclusi nel periodo — usati per calcolare il tempo medio di chiusura',
                <Clock className="h-5 w-5 text-indigo-600" />,
              )}
            />
          ),
          avg_value: (
            <DashboardCard
              key="avg_value"
              title="Valore medio offerta"
              value={<AnimatedCounter value={m.averageEstimateValue} prefix="€ " decimals={0} />}
              description={`Su ${m.totalEstimates} preventivi`}
              icon={<TrendingUp className="text-purple-600" />}
              change={buildChange(m.averageEstimateValue, prev?.averageEstimateValue)}
              onClick={() => setDrillDown({
                title: 'Tutti i preventivi del periodo',
                description: 'Usati per calcolare il valore medio offerta',
                icon: <TrendingUp className="h-5 w-5 text-purple-600" />,
                estimates: allInPeriod(),
              })}
            />
          ),
          lost: (
            <DashboardCard
              key="lost"
              title="Persi nel periodo"
              value={<AnimatedCounter value={m.lostValue} prefix="€ " decimals={0} />}
              description={`${m.lostEstimates} preventivi non andati a buon fine`}
              icon={<XCircle className="text-red-600" />}
              className="border-red-200/50 bg-gradient-to-br from-red-50/60 to-background"
              change={buildChange(m.lostValue, prev?.lostValue, { inversedDelta: true })}
              onClick={() => openStatusDrill(
                ['lost'],
                'Preventivi persi',
                'Preventivi marcati come persi nel periodo',
                <XCircle className="h-5 w-5 text-red-600" />,
              )}
            />
          ),
          margin: canSeeMargins && marginPctAvg !== null ? (
            <DashboardCard
              key="margin"
              title="Margine medio"
              value={<AnimatedCounter value={marginPctAvg} suffix=" %" decimals={0} />}
              description={
                marginPctAvg >= 30 ? 'Ottimo margine sui preventivi filtrati'
                : marginPctAvg >= 15 ? 'Margine sano'
                : marginPctAvg > 0 ? 'Margine basso — rivedere mark-up'
                : 'Margine negativo!'
              }
              icon={<Crosshair className={
                marginPctAvg >= 30 ? 'text-emerald-600'
                : marginPctAvg >= 15 ? 'text-amber-600'
                : 'text-red-600'
              } />}
              className={
                marginPctAvg >= 30 ? 'border-emerald-200/50 bg-gradient-to-br from-emerald-50/60 to-background'
                : marginPctAvg >= 15 ? 'border-amber-200/50 bg-gradient-to-br from-amber-50/60 to-background'
                : 'border-red-200/50 bg-gradient-to-br from-red-50/60 to-background'
              }
            />
          ) : null,
        };

        // Applica ordine personalizzato e nascondi quelle disabilitate.
        // Skippa anche le card che restituiscono null (es. 'margin' per non-admin).
        const visibleHero = layout.hero_cards_order
          .filter(c => !layout.hero_cards_hidden.includes(c))
          .filter(c => heroCards[c] !== null);
        // Adatta il numero di colonne in base a quante card sono visibili
        const colsClass = visibleHero.length >= 5
          ? 'xl:grid-cols-6'
          : visibleHero.length === 4
          ? 'xl:grid-cols-4'
          : visibleHero.length === 3
          ? 'xl:grid-cols-3'
          : 'xl:grid-cols-2';

        if (visibleHero.length === 0) {
          return (
            <div className="text-center py-6 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
              Tutte le card principali sono nascoste. Clicca <Settings2 className="inline h-3 w-3 mx-0.5" /> in alto per riattivarle.
            </div>
          );
        }

        return (
          <div
            className={`grid gap-4 md:grid-cols-2 lg:grid-cols-3 ${colsClass}`}
            data-tour-id="dashboard-kpi-strip"
          >
            {visibleHero.map(cardId => heroCards[cardId])}
          </div>
        );
      })()}

      {/* ============================================================
          ALERT: PREVENTIVI INVIATI > 30 GG SENZA RISPOSTA
          ============================================================ */}
      {m.staleEstimates.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40 animate-fade-in">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-base">
                  {m.staleEstimates.length} preventivi senza risposta da oltre {staleDaysThreshold} giorni
                </CardTitle>
              </div>
              <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300">
                {euro(m.staleEstimates.reduce((s, e) => s + e.totalAmount, 0))} totali
              </Badge>
            </div>
            <CardDescription>
              Considera di richiamare il cliente o segnare come perso per ripulire la pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {m.staleEstimates.slice(0, 5).map(e => (
                <button
                  key={e.id}
                  onClick={() => setDrillDown({
                    title: 'Preventivi senza risposta',
                    description: `Inviati da oltre ${staleDaysThreshold} giorni e non ancora chiusi (${m.staleEstimates.length} totali)`,
                    icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    estimates: (estimatesData as any[])
                      .filter(x => m.staleEstimates.some(s => s.id === x.id))
                      .map(x => ({
                        id: x.id,
                        name: x.name || 'Preventivo',
                        status: x.status,
                        total_amount: x.total_amount,
                        created_at: x.created_at,
                        projects: x.projects ? { name: x.projects.name } : null,
                      })),
                  })}
                  className="w-full text-left flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-amber-100/50 transition-colors"
                >
                  <span className="font-medium truncate">{e.name}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-amber-800 text-xs">
                      {Math.floor(e.daysSinceSent)} giorni fa
                    </span>
                    <span className="font-mono text-xs">{euro(e.totalAmount)}</span>
                  </div>
                </button>
              ))}
              {m.staleEstimates.length > 5 && (
                <div className="text-xs text-muted-foreground pt-1">
                  + altri {m.staleEstimates.length - 5} preventivi stale
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================================================
          ALERT: OFFERTE IN SCADENZA / SCADUTE (validity_days)
          ============================================================ */}
      {m.expiringEstimates.length > 0 && (() => {
        const expired = m.expiringEstimates.filter(e => e.expired);
        const expiringSoon = m.expiringEstimates.filter(e => !e.expired);
        const colorClass = expired.length > 0
          ? 'border-red-300 bg-red-50/40'
          : 'border-orange-300 bg-orange-50/40';
        const iconColor = expired.length > 0 ? 'text-red-600' : 'text-orange-600';
        const badgeColor = expired.length > 0
          ? 'bg-red-100 text-red-900 border-red-300'
          : 'bg-orange-100 text-orange-900 border-orange-300';
        const title = expired.length > 0
          ? `${expired.length} offerte scadute${expiringSoon.length > 0 ? ` + ${expiringSoon.length} in scadenza` : ''}`
          : `${expiringSoon.length} offerte in scadenza entro 5 giorni`;
        return (
          <Card className={`${colorClass} animate-fade-in`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <AlarmClock className={`h-5 w-5 ${iconColor}`} />
                  <CardTitle className="text-base">{title}</CardTitle>
                </div>
                <Badge variant="outline" className={badgeColor}>
                  {euro(m.expiringEstimates.reduce((s, e) => s + e.totalAmount, 0))} totali
                </Badge>
              </div>
              <CardDescription>
                La validità commerciale (offer_issued_at + validità offerta) sta per finire o è
                già passata. Considera di richiamare il cliente o estendere l'offerta.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5">
                {m.expiringEstimates.slice(0, 5).map(e => (
                  <button
                    key={e.id}
                    onClick={() => setDrillDown({
                      title: 'Offerte in scadenza',
                      description: `Offerte con validità commerciale scaduta o vicina (${m.expiringEstimates.length} totali)`,
                      icon: <CalendarClock className={`h-5 w-5 ${iconColor}`} />,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      estimates: (estimatesData as any[])
                        .filter(x => m.expiringEstimates.some(s => s.id === x.id))
                        .map(x => ({
                          id: x.id,
                          name: x.name || 'Preventivo',
                          status: x.status,
                          total_amount: x.total_amount,
                          created_at: x.created_at,
                          projects: x.projects ? { name: x.projects.name } : null,
                        })),
                    })}
                    className={`w-full text-left flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-white/60 transition-colors`}
                  >
                    <span className="font-medium truncate">{e.name}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs font-medium ${e.expired ? 'text-red-700' : 'text-orange-700'}`}>
                        {e.expired
                          ? `scaduta da ${Math.abs(e.daysToExpiry)} gg`
                          : e.daysToExpiry === 0
                          ? 'scade oggi'
                          : `scade fra ${e.daysToExpiry} gg`}
                      </span>
                      <span className="font-mono text-xs">{euro(e.totalAmount)}</span>
                    </div>
                  </button>
                ))}
                {m.expiringEstimates.length > 5 && (
                  <div className="text-xs text-muted-foreground pt-1">
                    + altre {m.expiringEstimates.length - 5} offerte
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ============================================================
          ALERT: PREVENTIVI CON PREZZI DISALLINEATI (auto-recompute)
          ============================================================ */}
      {m.pricesStaleEstimates.length > 0 && (
        <Card className="border-blue-300 bg-blue-50/40 animate-fade-in">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-base">
                  {m.pricesStaleEstimates.length} preventivi con prezzi disallineati al catalogo
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-900 border-blue-300">
                  {euro(m.pricesStaleEstimates.reduce((s, e) => s + e.totalAmount, 0))} totali
                </Badge>
                <Button
                  size="sm"
                  variant="default"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={isRefreshing}
                  onClick={() => refreshAll({ estimateIds: m.pricesStaleEstimates.map(e => e.id) })}
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Aggiornamento…' : 'Aggiorna tutti'}
                </Button>
              </div>
            </div>
            <CardDescription>
              Qualche prezzo materiale è cambiato dopo l'ultimo aggiornamento. Clicca "Aggiorna tutti"
              per riallineare in un colpo solo, oppure apri un preventivo specifico.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {m.pricesStaleEstimates.slice(0, 5).map(e => (
                <button
                  key={e.id}
                  onClick={() => setDrillDown({
                    title: 'Preventivi con prezzi disallineati',
                    description: `Aggiorna prezzi per riallinearli al catalogo (${m.pricesStaleEstimates.length} totali)`,
                    icon: <RefreshCw className="h-5 w-5 text-blue-600" />,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    estimates: (estimatesData as any[])
                      .filter(x => m.pricesStaleEstimates.some(s => s.id === x.id))
                      .map(x => ({
                        id: x.id,
                        name: x.name || 'Preventivo',
                        status: x.status,
                        total_amount: x.total_amount,
                        created_at: x.created_at,
                        projects: x.projects ? { name: x.projects.name } : null,
                      })),
                  })}
                  className="w-full text-left flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-blue-100/50 transition-colors"
                >
                  <span className="font-medium truncate">{e.name}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-blue-800 text-xs">
                      {e.staleRows} {e.staleRows === 1 ? 'riga' : 'righe'} da aggiornare
                    </span>
                    <span className="font-mono text-xs">{euro(e.totalAmount)}</span>
                  </div>
                </button>
              ))}
              {m.pricesStaleEstimates.length > 5 && (
                <div className="text-xs text-muted-foreground pt-1">
                  + altri {m.pricesStaleEstimates.length - 5} preventivi disallineati
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================================================
          STATO IMPRESA (sempre live, non filtrato da periodo)
          ============================================================ */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <DashboardCard
          title="Progetti attivi"
          value={<AnimatedCounter value={m.activeProjects} />}
          description={`${m.totalProjects} totali · ${m.completedProjects} completati`}
          icon={<Users />}
        />
        <DashboardCard
          title="Bozze in lavorazione"
          value={<AnimatedCounter value={m.draftEstimates} />}
          description="Preventivi mai inviati al cliente"
          icon={<FileEdit />}
        />
        <DashboardCard
          title="Superficie preventivata"
          value={<AnimatedCounter value={m.totalSurfaceArea} suffix=" m²" decimals={0} />}
          description={`€ ${formatNumber(m.averageCostPerSqm)}/m² medio`}
          icon={<Target />}
        />
        <DashboardCard
          title="Valore complessivo"
          value={<AnimatedCounter value={m.totalValue} prefix="€ " decimals={0} />}
          description="Tutti i preventivi del periodo"
          icon={<Euro />}
        />
      </div>

      {/* ============================================================
          TABS: PANORAMICA / COMMERCIALE / MATERIALI / OPERATIVO
          (F34: visibilità da layout)
          ============================================================ */}
      {(() => {
        const allTabs: { id: DashboardTabId; label: string }[] = [
          { id: 'overview', label: 'Panoramica' },
          { id: 'sales', label: 'Commerciale' },
          { id: 'materials', label: 'Materiali & Fornitori' },
          { id: 'operational', label: 'Operativo' },
        ];
        const visibleTabs = allTabs.filter(t => !layout.tabs_hidden.includes(t.id));
        if (visibleTabs.length === 0) {
          return (
            <div className="text-center py-6 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
              Tutti i tab sono nascosti. Clicca <Settings2 className="inline h-3 w-3 mx-0.5" /> in alto per riattivarli.
            </div>
          );
        }
        const defaultTab = visibleTabs[0].id;
        const gridColsClass = visibleTabs.length === 4 ? 'grid-cols-4'
          : visibleTabs.length === 3 ? 'grid-cols-3'
          : visibleTabs.length === 2 ? 'grid-cols-2'
          : 'grid-cols-1';
        return (
      <Tabs defaultValue={defaultTab} className="space-y-4" key={visibleTabs.map(t => t.id).join('-')}>
        <TabsList className={`grid w-full ${gridColsClass}`}>
          {visibleTabs.map(t => (
            <TabsTrigger key={t.id} value={t.id}>{t.label}</TabsTrigger>
          ))}
        </TabsList>

        {/* ========== TAB 1: PANORAMICA ========== */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            {/* Sales funnel */}
            <Card className="lg:col-span-2 animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  Funnel preventivi
                </CardTitle>
                <CardDescription>
                  Distribuzione del periodo per stato workflow F30
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hasAnyEstimate ? (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={funnelData} layout="vertical" margin={{ left: 10, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={80} />
                        <Tooltip
                          formatter={(value: number) => [`${value} preventivi`, '']}
                          cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        />
                        <Bar
                          dataKey="value"
                          radius={[0, 6, 6, 0]}
                          cursor="pointer"
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onClick={(data: any) => {
                            if (!data || data.value === 0) return;
                            // Click sul segmento → filtra tutta la dashboard per quello stato.
                            // Toggle: cliccare di nuovo lo rimuove.
                            const statuses = data.statuses as string[];
                            // Usa il primo status normalizzato come chiave del filtro
                            // (mappa pending→sent, contracted→won; vedi context).
                            const primary = statuses.includes('won') ? 'won'
                              : statuses.includes('sent') ? 'sent'
                              : statuses[0];
                            toggleStatus(primary);
                          }}
                        >
                          {funnelData.map((entry, idx) => (
                            <Cell key={`cell-${idx}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<FileText className="h-12 w-12 text-muted-foreground/30" />}
                    title="Nessun preventivo nel periodo"
                    description="Quando inizierai a creare preventivi, vedrai qui il funnel di vendita."
                  />
                )}
              </CardContent>
            </Card>

            {/* Motivi perdita */}
            <Card className="animate-fade-in" style={{ animationDelay: '0.05s' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  Motivi perdita
                </CardTitle>
                <CardDescription>
                  Perché i clienti dicono di no
                </CardDescription>
              </CardHeader>
              <CardContent>
                {m.lostReasonsBreakdown.length > 0 ? (
                  <div className="space-y-3">
                    {m.lostReasonsBreakdown.map((r, idx) => (
                      <button
                        key={r.reason}
                        onClick={() => openLostReasonDrill(r.reason)}
                        className="w-full text-left space-y-1 p-2 -mx-2 rounded hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{r.reason}</span>
                          <span className="text-muted-foreground">{r.count}</span>
                        </div>
                        <AnimatedProgress
                          value={r.count}
                          max={Math.max(...m.lostReasonsBreakdown.map(x => x.count))}
                          color={idx === 0 ? 'error' : 'primary'}
                        />
                        <div className="text-xs text-muted-foreground text-right">
                          {euro(r.value)}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Trophy className="h-12 w-12 text-emerald-300" />}
                    title="Nessun preventivo perso"
                    description="Niente perdite registrate nel periodo. Continua così!"
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trend mensile valore (12 mesi) — click su mese filtra dashboard */}
          <Card className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-primary" />
                    Andamento ultimi 12 mesi
                  </CardTitle>
                  <CardDescription>
                    Valore preventivato vs. effettivamente vinto, mese per mese
                  </CardDescription>
                </div>
                <div className="hidden md:flex items-center gap-3 text-xs">
                  <Legendina color="#3478BE" label="Preventivato" />
                  <Legendina color="#10b981" label="Vinto" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={m.monthlyTrends}
                    onClick={drillToMonth}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `€${formatNumber(v / 1000, 0)}k`} />
                    <Tooltip
                      formatter={(value: number, name: string) => [euro(value), name]}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#3478BE"
                      fill="#3478BE"
                      fillOpacity={0.2}
                      strokeWidth={2}
                      name="Preventivato"
                    />
                    <Line
                      type="monotone"
                      dataKey="wonValue"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Vinto"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-1">
                Suggerimento: clicca un mese per filtrare la dashboard a quel periodo
              </p>
            </CardContent>
          </Card>

          {/* Activity feed: ultime transizioni status — rispetta cross-filters */}
          <DashboardActivityFeed estimatesData={filteredEstimatesForWidgets} />

          {/* Forecast prossimo mese — regressione lineare sui wonValue ultimi 6 mesi */}
          {(() => {
            const series = m.monthlyTrends.slice(-6).map(t => t.wonValue);
            if (series.length < 3) return null;
            const n = series.length;
            const sumX = (n * (n - 1)) / 2;
            const sumY = series.reduce((s, v) => s + v, 0);
            const sumXY = series.reduce((s, v, i) => s + v * i, 0);
            const sumXX = series.reduce((s, _, i) => s + i * i, 0);
            const denom = n * sumXX - sumX * sumX;
            if (denom === 0) return null;
            const slope = (n * sumXY - sumX * sumY) / denom;
            const intercept = (sumY - slope * sumX) / n;
            const forecast = Math.max(0, slope * n + intercept);
            const lastValue = series[series.length - 1] ?? 0;
            const avgValue = sumY / n;
            const trendType = slope > avgValue * 0.05 ? 'up'
              : slope < -avgValue * 0.05 ? 'down'
              : 'flat';
            const trendLabel = trendType === 'up' ? 'in crescita'
              : trendType === 'down' ? 'in calo'
              : 'stabile';
            const trendColor = trendType === 'up' ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
              : trendType === 'down' ? 'text-red-600 bg-red-50 border-red-200'
              : 'text-slate-600 bg-slate-50 border-slate-200';
            // Mese successivo all'ultimo mostrato nel grafico
            const lastMonthDate = m.monthlyTrends.length > 0
              ? new Date(m.monthlyTrends[m.monthlyTrends.length - 1].monthEndIso)
              : new Date();
            const nextMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() + 1, 1);
            const nextMonthLabel = format(nextMonth, 'MMMM yyyy', { locale: itLocale });

            return (
              <Card className={`animate-fade-in border ${trendColor}`} style={{ animationDelay: '0.15s' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-5 w-5" />
                    Forecast prossimo mese: <span className="capitalize">{nextMonthLabel}</span>
                  </CardTitle>
                  <CardDescription>
                    Proiezione del valore vinto basata sul trend degli ultimi {n} mesi (regressione lineare)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">Stima vinto</div>
                      <div className="text-2xl font-bold tabular-nums">{euro(forecast)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">Ultimo mese</div>
                      <div className="text-xl font-semibold tabular-nums text-muted-foreground">{euro(lastValue)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">Trend</div>
                      <div className="text-xl font-semibold capitalize">{trendLabel}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {slope >= 0 ? '+' : ''}{euro(slope)}/mese
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground italic mt-3">
                    Forecast non probabilistico: estrapolazione lineare semplice, non considera stagionalità o eventi straordinari. Indicativo.
                  </p>
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>

        {/* ========== TAB 2: COMMERCIALE ========== */}
        <TabsContent value="sales" className="space-y-4">
          {/* Widget CRM: follow-up imminenti cross-project (7gg). Hidden se vuoto. */}
          <DashboardFollowUpWidget />

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Top clienti */}
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-emerald-600" />
                  Top 5 clienti per fatturato vinto
                </CardTitle>
                <CardDescription>
                  Chi ti porta più valore nel periodo selezionato
                </CardDescription>
              </CardHeader>
              <CardContent>
                {topCustomers.length > 0 ? (
                  <div className="space-y-3">
                    {topCustomers.map((c, idx) => {
                      const isFilterActive = filters.customerIds.includes(c.id);
                      return (
                      <button
                        key={c.id}
                        onClick={() => toggleCustomer(c.id)}
                        className={
                          'w-full text-left flex items-center gap-3 p-2 rounded transition-colors ' +
                          (isFilterActive ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'hover:bg-muted/50')
                        }
                        title={isFilterActive ? 'Clicca per rimuovere dal filtro' : 'Clicca per filtrare la dashboard su questo cliente'}
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          {c.city && (
                            <div className="text-xs text-muted-foreground">{c.city}</div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-semibold">{euro(c.totalWon)}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.count} {c.count === 1 ? 'lavoro' : 'lavori'}
                          </div>
                        </div>
                      </button>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Users className="h-12 w-12 text-muted-foreground/30" />}
                    title="Ancora nessun cliente vincente"
                    description="Collega i preventivi all'anagrafica clienti per vedere classifica."
                    cta={<Link to="/customers"><Button variant="outline" size="sm">Vai a Clienti <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>}
                  />
                )}
              </CardContent>
            </Card>

            {/* Top stratigrafie */}
            <Card className="animate-fade-in" style={{ animationDelay: '0.05s' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-blue-600" />
                  Top 5 stratigrafie più usate
                </CardTitle>
                <CardDescription>
                  Quali pareti usi più spesso nei preventivi
                </CardDescription>
              </CardHeader>
              <CardContent>
                {m.topStratigraphies.length > 0 ? (
                  <div className="space-y-3">
                    {m.topStratigraphies.map((s, idx) => {
                      const isFilterActive = filters.stratigraphyName === s.name;
                      return (
                      <button
                        key={s.name}
                        onClick={() => setStratigraphy(s.name)}
                        className={
                          'w-full text-left space-y-1.5 p-2 -mx-2 rounded transition-colors ' +
                          (isFilterActive ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-muted/50')
                        }
                        title={isFilterActive ? 'Clicca per rimuovere dal filtro' : 'Clicca per filtrare la dashboard su questa stratigrafia'}
                      >
                        <div className="flex items-center justify-between text-sm gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-bold text-muted-foreground w-5">#{idx + 1}</span>
                            <span className="font-medium truncate">{s.name}</span>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-semibold tabular-nums">{euro(s.totalValue)}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {s.usedCount}× · {formatNumber(s.totalArea, 0)} m²
                            </div>
                          </div>
                        </div>
                        <AnimatedProgress
                          value={s.usedCount}
                          max={Math.max(...m.topStratigraphies.map(x => x.usedCount))}
                          color="primary"
                        />
                      </button>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Layers className="h-12 w-12 text-muted-foreground/30" />}
                    title="Ancora nessuna stratigrafia usata"
                    description="Aggiungi stratigrafie ai tuoi preventivi per popolare questa classifica."
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Distribuzione preventivi (pie) */}
          <Card className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardHeader>
              <CardTitle>Composizione pipeline</CardTitle>
              <CardDescription>
                Quanti preventivi sono in ciascuno stato del workflow F30
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasAnyEstimate ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={funnelData.filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${formatNumber((percent || 0) * 100, 0)}%`}
                          outerRadius={90}
                          dataKey="value"
                          cursor="pointer"
                          // Click su una fetta → filtra la dashboard sullo stato corrispondente.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onClick={(data: any) => {
                            const statuses = (data?.statuses ?? data?.payload?.statuses) as string[] | undefined;
                            if (!statuses || statuses.length === 0) return;
                            const primary = statuses.includes('won') ? 'won'
                              : statuses.includes('sent') ? 'sent'
                              : statuses[0];
                            toggleStatus(primary);
                          }}
                        >
                          {funnelData.filter(d => d.value > 0).map((entry, idx) => {
                            const primary = entry.statuses.includes('won') ? 'won'
                              : entry.statuses.includes('sent') ? 'sent'
                              : entry.statuses[0];
                            const isFilterActive = filters.statuses.includes(primary);
                            return (
                              <Cell
                                key={`cell-${idx}`}
                                fill={entry.fill}
                                stroke={isFilterActive ? '#1f2937' : 'transparent'}
                                strokeWidth={isFilterActive ? 3 : 0}
                              />
                            );
                          })}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-col justify-center space-y-3">
                    <FunnelRow label="Bozze"   value={m.draftEstimates}      color={FUNNEL_COLORS.draft} icon={<FileEdit className="h-4 w-4" />}                                  onClick={() => toggleStatus('draft')} isActive={filters.statuses.includes('draft')} />
                    <FunnelRow label="Inviati" value={m.pendingEstimates}    color={FUNNEL_COLORS.sent}  icon={<Send className="h-4 w-4" />}     amount={m.pendingValue}        onClick={() => toggleStatus('sent')}  isActive={filters.statuses.includes('sent')} />
                    <FunnelRow label="Vinti"   value={m.contractedEstimates} color={FUNNEL_COLORS.won}   icon={<Trophy className="h-4 w-4" />}   amount={m.contractedValue}     onClick={() => toggleStatus('won')}   isActive={filters.statuses.includes('won')} />
                    <FunnelRow label="Persi"   value={m.lostEstimates}       color={FUNNEL_COLORS.lost}  icon={<XCircle className="h-4 w-4" />}  amount={m.lostValue}           onClick={() => toggleStatus('lost')}  isActive={filters.statuses.includes('lost')} />
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<FileText className="h-12 w-12 text-muted-foreground/30" />}
                  title="Nessun preventivo nel periodo"
                  description="Crea il tuo primo preventivo per iniziare a popolare la dashboard."
                  cta={<Link to="/projects"><Button variant="default" size="sm">Vai a Progetti <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>}
                />
              )}
            </CardContent>
          </Card>

          {/* Top clienti per win-rate — affidabilità di chiusura */}
          {topCustomersByWinRate.length > 0 && (
            <Card className="animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-600" />
                  Clienti più affidabili (win-rate)
                </CardTitle>
                <CardDescription>
                  Chi accetta i tuoi preventivi più spesso · min 3 chiusi (vinti + persi) per essere in classifica
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topCustomersByWinRate.map((c, idx) => {
                    const isFilterActive = filters.customerIds.includes(c.id);
                    const wrColor = c.winRate >= 70 ? 'text-emerald-600 bg-emerald-100'
                      : c.winRate >= 40 ? 'text-amber-600 bg-amber-100'
                      : 'text-red-600 bg-red-100';
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleCustomer(c.id)}
                        className={
                          'w-full text-left flex items-center gap-3 p-2 rounded transition-colors ' +
                          (isFilterActive ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'hover:bg-muted/50')
                        }
                        title={isFilterActive ? 'Clicca per rimuovere dal filtro' : 'Filtra dashboard su questo cliente'}
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.wonCount} vinti · {c.lostCount} persi · {euro(c.wonValue)} fatturato
                          </div>
                        </div>
                        <div className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-bold tabular-nums ${wrColor}`}>
                          {formatNumber(c.winRate, 0)}%
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== TAB 3: MATERIALI & FORNITORI ========== */}
        <TabsContent value="materials" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <MaterialsBreakdownCard
              data={m.categoryBreakdown}
              totalCost={m.totalMaterialsCost}
              estimatesData={filteredEstimatesForWidgets}
            />

            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Factory className="h-5 w-5 text-primary" />
                  Top fornitori del periodo
                </CardTitle>
                <CardDescription>
                  Chi sta movimentando più volume di acquisti
                </CardDescription>
              </CardHeader>
              <CardContent>
                {m.supplierBreakdown.length > 0 ? (
                  <div className="space-y-3">
                    {m.supplierBreakdown.slice(0, 5).map((s, idx) => {
                      const isFilterActive = filters.supplier === s.supplier;
                      return (
                      <button
                        key={s.supplier}
                        onClick={() => setSupplier(s.supplier)}
                        className={
                          'w-full text-left space-y-1.5 p-2 -mx-2 rounded transition-colors ' +
                          (isFilterActive ? 'bg-orange-50 ring-1 ring-orange-200' : 'hover:bg-muted/50')
                        }
                        title={isFilterActive ? 'Clicca per rimuovere dal filtro' : 'Clicca per filtrare la dashboard su questo fornitore'}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-bold text-muted-foreground w-5">#{idx + 1}</span>
                            <span className="font-medium truncate">{s.supplier}</span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-semibold">{euro(s.totalCost)}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {formatNumber(s.percentage, 0)}%
                            </span>
                          </div>
                        </div>
                        <AnimatedProgress
                          value={s.percentage}
                          max={100}
                          color="primary"
                        />
                      </button>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Factory className="h-12 w-12 text-muted-foreground/30" />}
                    title="Nessun fornitore movimentato"
                    description="I preventivi del periodo non hanno ancora materiali assegnati."
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ========== TAB 4: OPERATIVO ========== */}
        <TabsContent value="operational" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <DashboardCard
              title="Costo manodopera"
              value={<AnimatedCounter value={m.totalLaborCost} prefix="€ " decimals={0} />}
              description="Totale del periodo"
              icon={<Wrench />}
            />
            <DashboardCard
              title="Tempo posa medio"
              value={<AnimatedCounter value={m.avgInstallationTime} suffix=" min/m²" decimals={1} />}
              description="Su tutte le stratigrafie"
              icon={<Clock />}
            />
            <DashboardCard
              title="Sfrido medio"
              value={<AnimatedCounter value={m.wastePercentage} suffix=" %" decimals={1} />}
              description="Pesato sui materiali usati"
              icon={<AlertTriangle />}
              change={m.wastePercentage > 0 ? {
                value: m.wastePercentage - 10,
                type: m.wastePercentage > 10 ? 'negative' : 'positive',
                label: 'vs benchmark 10%',
              } : undefined}
            />
            <DashboardCard
              title="Costo medio €/m²"
              value={<AnimatedCounter value={m.averageCostPerSqm} prefix="€ " suffix="/m²" decimals={2} />}
              description="Materiali + manodopera"
              icon={<Target />}
            />
          </div>

          {/* Distribuzione superficie nei mesi */}
          <Card className="animate-fade-in" style={{ animationDelay: '0.05s' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                Superficie preventivata per mese
              </CardTitle>
              <CardDescription>
                m² totali nei preventivi creati ogni mese (ultimi 12)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={m.monthlyTrends}
                    onClick={drillToMonth}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => [`${formatNumber(v, 0)} m²`, 'Superficie']} />
                    <Area
                      type="monotone"
                      dataKey="area"
                      stroke="#3478BE"
                      fill="#3478BE"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-1">
                Suggerimento: clicca un mese per filtrare la dashboard a quel periodo
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        );
      })()}

      {/* F16.1 — Drill-down sheet: si apre quando l'utente clicca su una
          KPI / segmento / riga della dashboard */}
      <DashboardDrillDownSheet
        open={!!drillDown}
        onOpenChange={(open) => { if (!open) setDrillDown(null); }}
        title={drillDown?.title ?? ''}
        description={drillDown?.description}
        icon={drillDown?.icon}
        estimates={drillDown?.estimates ?? []}
      />

      {/* F34 — Sheet personalizzazione dashboard */}
      <DashboardCustomizeSheet
        open={openCustomize}
        onOpenChange={setOpenCustomize}
        layout={layout}
        onSave={saveLayout}
        onReset={resetLayout}
        isSaving={isSavingLayout}
      />
    </div>
  );
};

/**
 * Wrapper esportato: avvolge Inner nel Provider del cross-filtering.
 * Mantenuto come componente separato per garantire che `useDashboardFilters`
 * sia sempre dentro al Provider.
 */
export const EnhancedDashboardOverview = () => (
  <DashboardFiltersProvider>
    <EnhancedDashboardOverviewInner />
  </DashboardFiltersProvider>
);

// ============================================================
// SUB-COMPONENTS
// ============================================================

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta?: React.ReactNode;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description, cta }) => (
  <div className="flex flex-col items-center justify-center text-center py-10 px-4">
    <div className="mb-4">{icon}</div>
    <h4 className="font-semibold text-sm mb-1">{title}</h4>
    <p className="text-xs text-muted-foreground mb-3 max-w-xs">{description}</p>
    {cta}
  </div>
);

interface LegendinaProps { color: string; label: string }
const Legendina: React.FC<LegendinaProps> = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
    <span className="text-muted-foreground">{label}</span>
  </div>
);

interface FunnelRowProps {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  amount?: number;
  /** Toggle filtro stato sulla dashboard. Se assente, la riga resta non cliccabile. */
  onClick?: () => void;
  /** True quando lo stato è già nei filtri attivi: evidenzia bordo + sfondo. */
  isActive?: boolean;
}
const FunnelRow: React.FC<FunnelRowProps> = ({ label, value, color, icon, amount, onClick, isActive }) => {
  const baseBorder = isActive ? color : `${color}40`;
  const bg = isActive ? `${color}14` : undefined;
  const cls = 'w-full text-left flex items-center justify-between p-2 rounded border transition-colors ' +
    (onClick ? 'hover:bg-muted/50 cursor-pointer' : '');
  const content = (
    <>
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded" style={{ backgroundColor: `${color}20`, color }}>
          {icon}
        </div>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-right">
        <div className="font-bold">{value}</div>
        {amount !== undefined && amount > 0 && (
          <div className="text-[10px] text-muted-foreground">{euro(amount)}</div>
        )}
      </div>
    </>
  );
  if (!onClick) {
    return (
      <div className={cls.replace('w-full text-left ', '')} style={{ borderColor: baseBorder, backgroundColor: bg }}>
        {content}
      </div>
    );
  }
  return (
    <button
      onClick={onClick}
      className={cls}
      style={{ borderColor: baseBorder, backgroundColor: bg, borderWidth: isActive ? 2 : 1 }}
      title={isActive ? 'Clicca per rimuovere dal filtro' : `Filtra dashboard su "${label}"`}
    >
      {content}
    </button>
  );
};
